/**
 * M9 Tenant configuration editor REST client.
 */

import { env } from '@/lib/env';
import { ApiError, type ProblemDetail } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';

export type TenantConfiguration = {
  id: string;
  category: string;
  payload: Record<string, unknown>;
  version: number;
  updated_at: string;
};

export type TenantConfigurationVersion = {
  id: string;
  category: string;
  version: number;
  payload: Record<string, unknown>;
  changed_by_user_id: string | null;
  change_note: string | null;
  created_at: string;
};

export type AlertRule = {
  alert_type: string;
  is_enabled: boolean;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  thresholds: Record<string, unknown>;
  notify_channels: Record<string, unknown>;
};

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const url = `${env.VITE_API_URL}/api/v1${path}`;
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(url, { ...init, headers });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const problem: ProblemDetail = (data?.error ?? data) || {
      detail: `HTTP ${res.status}`,
    };
    throw new ApiError(res.status, problem);
  }
  return data as T;
}

export const CATEGORIES = [
  'terminology',
  'localization',
  'asset_types',
  'document_types',
  'workflow',
  'alerts',
  'roles',
  'salary',
  'engagement',
  'mobile',
  'notifications',
  'retention',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const tenantConfigApi = {
  catalog: () => call<{ categories: string[] }>('/tenant-config/_catalog'),
  listAll: () => call<TenantConfiguration[]>('/tenant-config'),
  get: (category: string) => call<TenantConfiguration>(`/tenant-config/${category}`),
  patch: (
    category: string,
    payload: Record<string, unknown>,
    opts?: { change_note?: string; replace?: boolean },
  ) =>
    call<TenantConfiguration>(`/tenant-config/${category}`, {
      method: 'PATCH',
      body: JSON.stringify({
        payload,
        change_note: opts?.change_note,
        replace: opts?.replace ?? false,
      }),
    }),
  history: (category: string, limit = 50) =>
    call<TenantConfigurationVersion[]>(`/tenant-config/${category}/history?limit=${limit}`),
  rollback: (category: string, target_version: number) =>
    call<TenantConfiguration>(`/tenant-config/${category}:rollback`, {
      method: 'POST',
      body: JSON.stringify({ target_version }),
    }),
  listAlertRules: () => call<AlertRule[]>('/tenant-config/alerts/rules'),
  upsertAlertRule: (rule: AlertRule) =>
    call<AlertRule>('/tenant-config/alerts/rules', {
      method: 'PUT',
      body: JSON.stringify(rule),
    }),
};
